import { TextField } from "@shared/components/TextField";
import {
  UserBrandingSchema,
  userBrandingFormSchema,
} from "./user-branding-form-helpers";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid } from "@mui/material";
import { Form } from "@shared/components/Form";
import MaskedInput from "react-text-mask";
import { phoneNumberMask } from "@shared/utils/input-masks";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { useUpdateUserBrandingImage } from "@queries/profile/useUpdateUserProfileOfficeLogo";
import { ImageUploadWithPreview } from "../../ImageUploadWithPreview";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
// import { ImagePreviewField } from "../../ImagePreviewField";
// import InstagramIcon from "@mui/icons-material/Instagram";
// import FacebookIcon from "@mui/icons-material/Facebook";
// import XIcon from "@mui/icons-material/X";
// import LinkedInIcon from "@mui/icons-material/LinkedIn";
// import YouTubeIcon from "@mui/icons-material/YouTube";

type UserBrandingFormModel = {
  agentName: string | null;
  agentEmail: string | null;
  agentTitle: string | null;
  agentAvatar: string | null;
  agentMlsId: string | null;
  agentPhoneNumber: string | null;
  officeName: string | null;
  officeLogo: string | null;
  website: string | null;
  teamName: string | null;
  teamLogo: string | null;
  instagramUrl: string | null;
  facebookUrl: string | null;
  xUrl: string | null;
  youtubeUrl: string | null;
  linkedInUrl: string | null;
};

export type UserBrandingFormProps = {
  upsertUserBranding: (data: UserBrandingSchema) => void;
  isUpsertUserBrandingPending: boolean;
  defaultValues: UserBrandingFormModel;
};

export function UserBrandingForm(props: UserBrandingFormProps) {
  const { defaultValues, upsertUserBranding, isUpsertUserBrandingPending } =
    props;
  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
  } = useForm<UserBrandingSchema>({
    defaultValues,
    resolver: yupResolver(userBrandingFormSchema),
    reValidateMode: "onChange",
  });

  const onSubmit = (data: UserBrandingSchema) => {
    if (!isDirty) return;
    upsertUserBranding({
      agentName: data.agentName || null,
      agentEmail: data.agentEmail || null,
      agentTitle: data.agentTitle || null,
      agentMlsId: data.agentMlsId || null,
      agentPhoneNumber: data.agentPhoneNumber || null,
      officeName: data.officeName || null,
      website: data.website || null,
      teamName: data.teamName || null,
      instagramUrl: data.instagramUrl || null,
      facebookUrl: data.facebookUrl || null,
      xUrl: data.xUrl || null,
      youtubeUrl: data.youtubeUrl || null,
      linkedInUrl: data.linkedInUrl || null,
    });
    reset(data);
  };

  const userBrandingAgentAvatarMutation = useUpdateUserBrandingImage({
    field: "agentAvatar",
  });
  const onRemoveUserBrandingAgentAvatar = () => {
    upsertUserBranding({ agentAvatar: null });
  };

  const userBrandingOfficeLogoMutation = useUpdateUserBrandingImage({
    field: "officeLogo",
  });
  const onRemoveUserBrandingOfficeLogo = () => {
    upsertUserBranding({ officeLogo: null });
  };

  const userBrandingTeamLogoMutation = useUpdateUserBrandingImage({
    field: "teamLogo",
  });
  const onRemoveUserBrandingTeamLogo = () => {
    upsertUserBranding({ teamLogo: null });
  };

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      maxWidth="100%"
      aria-disabled={isUpsertUserBrandingPending}
    >
      <Box>
        <Grid
          container
          rowSpacing={3}
          columnSpacing={{ xs: 3, md: 6 }}
          alignItems={"flex-start"}
        >
          <Grid item xs={12} md={3} order={{ xs: 1, md: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                margin: "auto",
              }}
              mt={{ xs: 1, md: 0 }}
            >
              {/* <ImagePreviewField
                label="Profile Photo"
                imageSrc={user.avatar}
                alt={user.name}
                helperText="Same as your profile"
              /> */}
              <ImageUploadWithPreview
                label="Profile Photo"
                imageSrc={defaultValues.agentAvatar}
                alt={defaultValues.agentAvatar ?? ""}
                onSave={userBrandingAgentAvatarMutation.mutateAsync}
                onRemove={onRemoveUserBrandingAgentAvatar}
                isLoading={userBrandingAgentAvatarMutation.isPending}
                aspectRatio={1}
                placeholderIcon={<CameraAltOutlinedIcon />}
              />
            </Box>
          </Grid>

          <Grid
            container
            item
            columnSpacing={3}
            rowSpacing={1}
            xs={12}
            md={9}
            order={{ xs: 2, md: 1 }}
          >
            <Grid item xs={12} md={6}>
              <TextField
                name="agentName"
                label="Name"
                inputProps={{ ...register("agentName") }}
                fieldError={errors.agentName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="agentEmail"
                label="Contact E-mail"
                type="email"
                inputProps={{ ...register("agentEmail") }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="agentTitle"
                label="Your Title"
                inputProps={{ ...register("agentTitle") }}
                fieldError={errors.agentTitle}
                placeholder="Real Estate Agent"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MaskedInput
                mask={phoneNumberMask}
                render={(ref, props) => (
                  <TextField
                    InputProps={{ inputRef: ref }}
                    {...props}
                    label="Phone number"
                    name="agentPhoneNumber"
                    inputProps={{ ...register("agentPhoneNumber") }}
                    fieldError={errors.agentPhoneNumber}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="instagramUrl"
                label="Instagram"
                inputProps={{ ...register("instagramUrl") }}
                fieldError={errors.instagramUrl}
                placeholder="https://www.instagram.com/sidekickdata"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="facebookUrl"
                label="Facebook"
                inputProps={{ ...register("facebookUrl") }}
                fieldError={errors.facebookUrl}
                placeholder="https://www.facebook.com/sidekickdata/"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="linkedInUrl"
                label="LinkedIn"
                inputProps={{ ...register("linkedInUrl") }}
                fieldError={errors.linkedInUrl}
                placeholder="https://www.linkedin.com/in/sidekickdata/"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="xUrl"
                label="X"
                inputProps={{ ...register("xUrl") }}
                fieldError={errors.xUrl}
                placeholder="https://x.com/sidekickdata"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="youtubeUrl"
                label="YouTube"
                inputProps={{ ...register("youtubeUrl") }}
                fieldError={errors.youtubeUrl}
                placeholder="https://www.youtube.com/@YouTube"
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Brokerage Information */}
      <Box mb={2} mt={4}>
        <Grid
          container
          rowSpacing={3}
          columnSpacing={{ xs: 3, md: 6 }}
          alignItems={"flex-start"}
        >
          <Grid item xs={12} md={3} order={{ xs: 1, md: 2 }}>
            <ImageUploadWithPreview
              label="Brokerage Logo"
              imageSrc={defaultValues.officeLogo}
              alt={defaultValues.officeName ?? ""}
              onSave={userBrandingOfficeLogoMutation.mutateAsync}
              onRemove={onRemoveUserBrandingOfficeLogo}
              isLoading={userBrandingOfficeLogoMutation.isPending}
            />
          </Grid>

          <Grid
            container
            item
            columnSpacing={3}
            rowGap={1}
            xs={12}
            md={9}
            order={{ xs: 2, md: 1 }}
            sx={{ mt: { xs: 0, md: 0 } }}
          >
            <Grid item xs={12} md={6}>
              <TextField
                name="officeName"
                label="Brokerage Name"
                placeholder="Brokerage Name"
                inputProps={{ ...register("officeName") }}
                fieldError={errors.officeName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="website"
                label="Website"
                inputProps={{ ...register("website") }}
                fieldError={errors.website}
                placeholder="https://example.com"
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Team Information */}
      <Box mt={2}>
        <Grid
          container
          rowSpacing={3}
          columnSpacing={{ xs: 3, md: 6 }}
          alignItems={"flex-start"}
        >
          <Grid item xs={12} md={3} order={{ xs: 1, md: 2 }}>
            <ImageUploadWithPreview
              label="Team Logo"
              imageSrc={defaultValues.teamLogo}
              alt={defaultValues.teamLogo ?? ""}
              onSave={userBrandingTeamLogoMutation.mutateAsync}
              onRemove={onRemoveUserBrandingTeamLogo}
              isLoading={userBrandingTeamLogoMutation.isPending}
            />
          </Grid>

          <Grid
            container
            item
            columnSpacing={3}
            rowSpacing={1}
            xs={12}
            md={9}
            order={{ xs: 2, md: 1 }}
          >
            <Grid item xs={12} md={12}>
              <TextField
                name="teamName"
                label="Team Name"
                placeholder="Team Name"
                inputProps={{ ...register("teamName") }}
                fieldError={errors.teamName}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <SubmitButton
        sx={{ justifySelf: "flex-start", gap: 1.5, minWidth: "160px" }}
        isLoading={isUpsertUserBrandingPending}
        disabled={!isDirty}
        loadingText="Saving..."
      >
        Save
      </SubmitButton>
    </Form>
  );
}
